import React, { useState } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { FormProps } from '~types'
import { Button, ConditionalWrapper, Heading, Paragraph, Modal } from '~elements'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { useModal } from 'utils/hooks/useModal'
import { FORM_ERROR_VIEW } from 'constants/modal-form-error'
import { isValidEmail } from 'utils'
import styles from './Form.module.scss'
import cx from 'classnames'

// @ts-ignore
const ReactFormstack = dynamic(() => import('@msgtechnology/react-formstack').then(module => module.default), {
  ssr: false
}) as unknown as React.ComponentType<Record<string, unknown>>

interface FormstackData {
  id: string
}

const Form = ({
  formId,
  isChildOfModal = false,
  submitButtonBgColor = 'white',
  submitButtonText = 'Sign Up Now',
  formSuccessLogo,
  modalSuccessMessage,
  modalSuccessTitle,
  onCloseParentModal,
  successModalView,
  fieldEnhancements = {},
  floatingLabel = false,
  narrowForm = false
}: FormProps) => {
  const { isModalOpen, openModal, closeModal } = useModal()
  const [isSuccessful, setIsSuccessful] = useState<boolean | null>(null) // null = no submission yet
  const [updatedSuccessView, setUpdatedSuccessView] = useState(successModalView)

  const formStackApiUrl = process.env.NEXT_PUBLIC_FORMSTACK_API_URL
  if (!formStackApiUrl || !formId) return null

  const afterSubmit = (data: FormstackData & { data?: { field: string; value: string }[] }) => {
    const isSuccessful = data?.id !== undefined
    setIsSuccessful(isSuccessful)

    if (isSuccessful && successModalView?.description?.html) {
      const emailField = data?.data?.find(item => typeof item.value === 'string' && isValidEmail(item.value))

      if (emailField?.value) {
        const newHtml = successModalView.description.html.replace(/{emailAddress}/g, emailField.value)

        setUpdatedSuccessView({
          ...successModalView,
          description: {
            ...successModalView.description,
            html: newHtml
          }
        })
      }
    }

    if (!isChildOfModal) {
      openModal()
    }
  }

  const formClassNames = cx([
    'formstack',
    'formstack--input-bg-color-transparent',
    `formstack--submit-btn-bg-${submitButtonBgColor}`,
    isChildOfModal && 'formstack--is-child-of-modal',
    styles['fs-field-wrapper'],
    styles['fs-label'],
    floatingLabel && 'floating-label',
    narrowForm && 'narrow-form'
  ])

  const modalView = isSuccessful ? updatedSuccessView : FORM_ERROR_VIEW
  const modalType = isSuccessful ? 'form_success' : 'form_error'

  const FEisEmpty =
    !fieldEnhancements ||
    (Object.getPrototypeOf(fieldEnhancements) === Object.prototype && Object.keys(fieldEnhancements).length === 0)

  const defaults = {
    email: {},
    phone: {},
    text: {},
    textarea: {},
    name: {},
    select: {
      useSelect2Ui: false
    },
    radio: {},
    checkbox: {},
    datetime: {
      validationMessage: 'Please enter a valid date.'
    }
  }

  const formFieldEnhancements = FEisEmpty ? defaults : fieldEnhancements ?? defaults
  return (
    <>
      {/* Show form if not submitted yet OR inside a modal */}
      {(isSuccessful === null || !isChildOfModal) && (
        <ReactFormstack
          className={formClassNames}
          id={formId.toString()}
          onAfterSubmit={afterSubmit}
          submitButtonText={submitButtonText}
          endpoint={formStackApiUrl}
          fieldEnhancements={formFieldEnhancements}
          disableBrowserValidation={true}
          shouldResetOnSubmit={true}
        />
      )}

      {isSuccessful !== null && (
        <ConditionalWrapper
          condition={!isChildOfModal}
          wrapper={children => (
            <Modal
              isOpen={isModalOpen}
              handleModalClose={closeModal}
              closeOnOutsideClick
              view={modalView}
              type={modalType}
            >
              {children}
            </Modal>
          )}
        >
          <>
            {isSuccessful && (
              <>
                {formSuccessLogo?.url && (
                  <Image
                    width={54}
                    height={54}
                    alt={formSuccessLogo?.altText || ''}
                    sizes='100vw'
                    src={formSuccessLogo.url}
                    quality={NEXT_IMAGE_QUALITY}
                  />
                )}
                {modalSuccessTitle && (
                  <Heading level={4} justify='center'>
                    {modalSuccessTitle}
                  </Heading>
                )}
                {modalSuccessMessage && <Paragraph text={modalSuccessMessage} justify='center' />}
                <Button
                  color='light'
                  fill='hollow'
                  justify='center'
                  size='lg'
                  onClick={isChildOfModal ? onCloseParentModal : closeModal}
                  title='Close'
                  analytics={{ skipEvent: true }}
                />
              </>
            )}
          </>
        </ConditionalWrapper>
      )}
    </>
  )
}

export default Form
